import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { setFullstoryPageProperties } from '@frontend/utils/fullstory';

import { useInstanceId } from './useInstanceId';

// Derive the top-level app section from the path. Routes are shaped like
// /instances/:instanceId/<section>/... so the section is the third segment;
// fall back to the first segment for non-instance routes (e.g. /instances).
export const getAppSection = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'instances' && parts.length >= 3) {
    return parts[2];
  }
  return parts[0] ?? 'root';
};

/**
 * Enrich the current Fullstory page with context that lives in the URL — the
 * active instance id and app section — on every route change. Fullstory already
 * captures the full URL automatically; this makes the context queryable as
 * first-class page properties. A no-op when Fullstory is disabled.
 */
export const useFullstoryPageContext = (): void => {
  const { instanceId } = useInstanceId();
  const { pathname } = useLocation();

  useEffect(() => {
    const properties: Record<string, string> = { section: getAppSection(pathname) };
    if (instanceId) {
      properties.instanceId = instanceId;
    }
    setFullstoryPageProperties(properties);
  }, [instanceId, pathname]);
};
